import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import './ProjectDetail.css';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchProject = useCallback(async () => {
    try {
      const response = await api.get(`/api/v1/projects/${id}`);
      setProject(response.data.project);
    } catch (err) {
      setError('Failed to load project');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await api.delete(`/api/v1/projects/${id}`);
      navigate('/dashboard');
    } catch (err) {
      alert('Failed to delete project');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await api.post(`/api/v1/uploads/project/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProject({ ...project, image_url: response.data.imageUrl });
    } catch (err) {
      alert('Failed to upload image');
    }
  };

  const handleImageDelete = async () => {
    if (!window.confirm('Are you sure you want to remove this image?')) {
      return;
    }

    try {
      await api.delete(`/api/v1/uploads/project/${id}`);
      setProject({ ...project, image_url: null });
    } catch (err) {
      alert('Failed to delete image');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !project) {
    return <div className="error-message">{error || 'Project not found'}</div>;
  }

  const progressData = [
    { name: 'Completed', value: project.progress_percentage, color: '#10b981' },
    { name: 'Remaining', value: 100 - project.progress_percentage, color: '#e5e7eb' },
  ];

  return (
    <div className="project-detail">
      <div className="project-detail-header">
        <Link to="/dashboard" className="back-link">← Back to Dashboard</Link>
        <div className="project-actions-header">
          <Link to={`/projects/${id}/edit`} className="btn btn-primary">
            Edit
          </Link>
          <button onClick={handleDelete} className="btn btn-danger">
            Delete
          </button>
        </div>
      </div>

      <div className="project-detail-content">
        <div className="project-main">
          <h1>{project.name}</h1>
          
          {project.image_url ? (
            <div className="project-image-container">
              <img src={project.image_url} alt={project.name} className="project-detail-image" />
              <button onClick={handleImageDelete} className="btn btn-danger btn-small">
                Remove Image
              </button>
            </div>
          ) : (
            <div className="image-upload-section">
              <label htmlFor="image-upload" className="btn btn-secondary">
                Upload Image
              </label>
              <input
                id="image-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </div>
          )}

          {project.description && (
            <div className="project-description">
              <h2>Description</h2>
              <p>{project.description}</p>
            </div>
          )}

          <div className="project-info">
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className={`status-badge status-${project.status}`}>
                {project.status.charAt(0).toUpperCase() + project.status.slice(1).replace('-', ' ')}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Created:</span>
              <span>{new Date(project.created_at).toLocaleDateString()}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Last Updated:</span>
              <span>{new Date(project.updated_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        <div className="project-progress">
          <h2>Progress</h2>
          <div className="progress-chart">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={progressData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                >
                  {progressData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <div className="progress-percentage">
              <span className="progress-number">{project.progress_percentage}%</span>
              <span className="progress-label">Complete</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
